import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle } from 'lucide-react'
import { TAGEBUCH_WETTER_LAGEN, type TagebuchBehinderungArt, type TagebuchNiederschlag, type TagebuchWetterLage, type TagebuchWind } from '@symphenia/shared-types'
import { fmtDate } from '@/lib/format-date'
import type { ErpVorzeigeProps } from '../registry'
import { zahlAnzeige } from './inhalt'

/** Der kanonische Fachteil, wie `tagebuchFachteil` ihn baut (AP-48 5.1) — Zahlen als Text. */
interface KanonTagebuch {
  arbeitszeit?: { beginn: string | null; ende: string | null }
  wetter?: { lage: TagebuchWetterLage[]; tempMorgen: string | null; tempMittag: string | null; niederschlag: TagebuchNiederschlag | null; wind: TagebuchWind | null; unterbrechung: { von: string; bis: string; grund: string } | null }
  stillstand?: string | null
  belegschaft?: { eigene: { qualifikation: string; anzahl: string; stunden: string }[]; namen: string[] | null; fremd: { firma: string; anzahl: string; taetigkeit: string | null }[] }
  geraete?: { bezeichnung: string; artikelnummer: string | null; anzahl: string; stunden: string | null }[]
  leistungen?: string[]
  lieferungen?: { text: string; lieferant: string | null; lieferschein: string | null }[]
  behinderungen?: { nr: number; art: TagebuchBehinderungArt; text: string; seit: string; bis: string | null; auswirkung: string | null; mehrkosten: boolean; angezeigtAm: string | null }[]
  anordnungen?: { von: string; text: string }[]
  vorkommnisse?: string | null
  bemerkung?: string | null
}

function Abschnitt({ titel, children }: { titel: string; children: ReactNode }) {
  return (
    <section>
      <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{titel}</h4>
      {children}
    </section>
  )
}

/**
 * AP-48 12. — **Vorzeige und eingefrorener Stand des Bautagesberichts im ERP**, aus dem kanonischen Fachteil:
 * Arbeitszeit, bei Stillstand groß der Grund, Wetter in einer Zeile, Belegschaft eigene/fremde mit Summe,
 * Geräte, Leistungen, Lieferungen, Behinderungen hervorgehoben („dauert an seit …"), Anordnungen,
 * Vorkommnisse, Bemerkung. Datum und Projekt stehen im Kopf des Rahmens.
 */
export function TagebuchErpVorzeige({ fachteil }: ErpVorzeigeProps) {
  const { t } = useTranslation()
  const f = fachteil as KanonTagebuch
  const w = f.wetter
  const eigene = f.belegschaft?.eigene ?? []
  const fremd = f.belegschaft?.fremd ?? []
  const namen = f.belegschaft?.namen ?? null
  const geraete = f.geraete ?? []
  const leistungen = f.leistungen ?? []
  const lieferungen = f.lieferungen ?? []
  const behinderungen = f.behinderungen ?? []
  const anordnungen = f.anordnungen ?? []
  const personen = [...eigene, ...fremd].reduce((s, z) => s + (Number(z.anzahl) || 0), 0)
  const stunden = eigene.reduce((s, z) => s + (Number(z.stunden) || 0), 0)

  const wetterTeile: string[] = []
  if (w?.lage?.length) wetterTeile.push(TAGEBUCH_WETTER_LAGEN.filter((l) => w.lage.includes(l)).map((l) => t(`siteDocs.diary.weather.${l}`)).join(', '))
  if (w && (w.tempMorgen !== null || w.tempMittag !== null)) wetterTeile.push(`${w.tempMorgen ?? '–'} °C / ${w.tempMittag ?? '–'} °C`)
  if (w?.niederschlag) wetterTeile.push(t(`siteDocs.diary.precipitation.${w.niederschlag}`))
  if (w?.wind) wetterTeile.push(t(`siteDocs.diary.wind.${w.wind}`))
  const zelle = 'py-1 pr-2'

  return (
    <div className="space-y-3 text-sm">
      {(f.arbeitszeit?.beginn || f.arbeitszeit?.ende) && (
        <p>
          <span className="text-muted-foreground">{t('siteDocs.diary.workTime')}:</span> {f.arbeitszeit?.beginn ?? '–'}–{f.arbeitszeit?.ende ?? '–'}
        </p>
      )}
      {f.stillstand && (
        <p className="rounded-md border-2 border-amber-400 bg-amber-50 px-3 py-2 text-base font-semibold text-amber-900 dark:bg-amber-900/30 dark:text-amber-100">
          {t('siteDocs.diary.standstillShown', { grund: f.stillstand })}
        </p>
      )}
      {wetterTeile.length > 0 && (
        <p>
          <span className="text-muted-foreground">{t('siteDocs.diary.weatherTitle')}:</span> {wetterTeile.join(' · ')}
        </p>
      )}
      {w?.unterbrechung && (
        <p>
          <span className="text-muted-foreground">{t('siteDocs.diary.interruption')}:</span> {w.unterbrechung.von}–{w.unterbrechung.bis} · {w.unterbrechung.grund}
        </p>
      )}

      {(eigene.length > 0 || fremd.length > 0) && (
        <Abschnitt titel={t('siteDocs.diary.staff')}>
          <table className="w-full">
            <thead>
              <tr className="text-left text-xs text-muted-foreground">
                <th className={zelle}>{t('siteDocs.diary.staffWho')}</th>
                <th className={`${zelle} text-right`}>{t('siteDocs.diary.count')}</th>
                <th className={`${zelle} text-right`}>{t('siteDocs.diary.hours')}</th>
              </tr>
            </thead>
            <tbody>
              {eigene.map((e, i) => (
                <tr key={`e${i}`} className="border-b border-border">
                  <td className={zelle}>{e.qualifikation}</td>
                  <td className={`${zelle} text-right tabular-nums`}>{zahlAnzeige(e.anzahl)}</td>
                  <td className={`${zelle} text-right tabular-nums`}>{zahlAnzeige(e.stunden)} h</td>
                </tr>
              ))}
              {fremd.map((x, i) => (
                <tr key={`f${i}`} className="border-b border-border">
                  <td className={zelle}>
                    <span className="font-medium">{x.firma}</span>
                    {x.taetigkeit && <span className="ml-2 text-xs text-muted-foreground">{x.taetigkeit}</span>}
                    <span className="ml-2 text-xs text-muted-foreground">({t('siteDocs.diary.external')})</span>
                  </td>
                  <td className={`${zelle} text-right tabular-nums`}>{zahlAnzeige(x.anzahl)}</td>
                  <td className={zelle} />
                </tr>
              ))}
              <tr className="font-semibold">
                <td className={zelle}>{t('siteDocs.diary.total')}</td>
                <td className={`${zelle} text-right tabular-nums`}>{personen}</td>
                <td className={`${zelle} text-right tabular-nums`}>{zahlAnzeige(stunden)} h</td>
              </tr>
            </tbody>
          </table>
          {namen && namen.length > 0 && <p className="mt-1 text-xs text-muted-foreground">{namen.join(', ')}</p>}
        </Abschnitt>
      )}

      {geraete.length > 0 && (
        <Abschnitt titel={t('siteDocs.diary.equipment')}>
          <ul className="space-y-0.5">
            {geraete.map((g, i) => (
              <li key={i}>
                {zahlAnzeige(g.anzahl)} × {g.bezeichnung}
                {g.artikelnummer && <span className="ml-2 text-xs text-muted-foreground">{g.artikelnummer}</span>}
                {g.stunden && <span className="ml-2 text-muted-foreground">· {zahlAnzeige(g.stunden)} h</span>}
              </li>
            ))}
          </ul>
        </Abschnitt>
      )}

      {leistungen.length > 0 && (
        <Abschnitt titel={t('siteDocs.diary.work')}>
          <ul className="list-disc space-y-0.5 pl-5">
            {leistungen.map((l, i) => (
              <li key={i}>{l}</li>
            ))}
          </ul>
        </Abschnitt>
      )}

      {lieferungen.length > 0 && (
        <Abschnitt titel={t('siteDocs.diary.deliveries')}>
          <ul className="space-y-0.5">
            {lieferungen.map((l, i) => (
              <li key={i}>
                {l.text}
                {(l.lieferant || l.lieferschein) && <span className="ml-2 text-muted-foreground">{[l.lieferant, l.lieferschein ? t('siteDocs.diary.deliveryNoteShown', { nr: l.lieferschein }) : null].filter(Boolean).join(' · ')}</span>}
              </li>
            ))}
          </ul>
        </Abschnitt>
      )}

      {behinderungen.length > 0 && (
        <Abschnitt titel={t('siteDocs.diary.hindrances')}>
          <ul className="space-y-2">
            {behinderungen.map((b) => (
              <li key={b.nr} className="rounded-md border-l-4 border-amber-500 bg-amber-50 px-3 py-2 dark:bg-amber-900/30">
                <p className="flex items-center gap-1 font-medium">
                  <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
                  {t(`siteDocs.diary.hindranceKinds.${b.art}`)}: {b.text}
                </p>
                <p className="text-xs text-muted-foreground">
                  {[
                    b.bis ? t('siteDocs.diary.fromTo', { von: fmtDate(b.seit), bis: fmtDate(b.bis) }) : t('siteDocs.diary.ongoingSince', { seit: fmtDate(b.seit) }),
                    b.auswirkung ? t('siteDocs.diary.effectShown', { text: b.auswirkung }) : null,
                    b.mehrkosten ? t('siteDocs.diary.extraCostsExpected') : null,
                    b.angezeigtAm ? t('siteDocs.diary.notifiedOnShown', { datum: fmtDate(b.angezeigtAm) }) : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              </li>
            ))}
          </ul>
        </Abschnitt>
      )}

      {anordnungen.length > 0 && (
        <Abschnitt titel={t('siteDocs.diary.orders')}>
          <ul className="space-y-0.5">
            {anordnungen.map((a, i) => (
              <li key={i}>
                <span className="text-muted-foreground">{a.von}:</span> {a.text}
              </li>
            ))}
          </ul>
        </Abschnitt>
      )}

      {f.vorkommnisse && (
        <Abschnitt titel={t('siteDocs.diary.incidents')}>
          <p className="whitespace-pre-line">{f.vorkommnisse}</p>
        </Abschnitt>
      )}
      {f.bemerkung && (
        <Abschnitt titel={t('siteDocs.diary.remark')}>
          <p className="whitespace-pre-line">{f.bemerkung}</p>
        </Abschnitt>
      )}
    </div>
  )
}
